import { z } from 'zod/v4';

/**
 * Sort columns the admin users list endpoint accepts. Each key maps to a SQL
 * expression in `core/db/users/list-admin.ts` (`SORT_COLUMNS`) — keep the two
 * in sync when adding/removing columns.
 */
export const AdminUsersListSortColumns = [
  'firstName',
  'lastName',
  'role',
  'company',
  'primaryCompany',
  'createdAt',
  'updatedAt',
  'email',
  'isBlocked',
] as const;

export type AdminUsersListSortColumn =
  (typeof AdminUsersListSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const AdminUsersListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  q: z.string().max(255).optional(),
  filter: z.string().max(512).optional(),
  withDelegatedUsers: booleanLike,
});

export type AdminUsersListQuery = z.infer<typeof AdminUsersListQuerySchema>;

/**
 * Row shape returned by the delegated-users admin list. `looseObject` because
 * the SQL plus per-user enrichment returns ~20 fields and adding strict typing
 * here duplicates `backend/src/types/users` without buying validation
 * strength for an admin-only read endpoint.
 *
 * Field reads in the FE (must stay populated):
 *   id, firstName, lastName, avatarFileUrl, hasAgreedWithTerms,
 *   hasAgreedWithTermsAt, primaryRole, role, primaryCompanyName,
 *   developmentTag, totalUserCompanies, email
 */
export const AdminUserListRowSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
});

export type AdminUserListRow = z.infer<typeof AdminUserListRowSchema>;

export const AdminUsersListResponseSchema = z.object({
  data: z.array(AdminUserListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AdminUsersListResponse = z.infer<
  typeof AdminUsersListResponseSchema
>;
